import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { MaxWidthStyled, mq } from '@hangar31/rc-layout';
import toColor from '@OnArk/utils/toColor';

// ---------------------------------------------------------------------------
// v2 auth layout — used by /login and /sign-up. Two-column shell with a
// branded gradient panel on the left and a clean form panel on the right.
// Stacks on mobile.
// ---------------------------------------------------------------------------

export const AuthShellStyled = styled.section(
  ({ theme }) => css`
    display: grid;
    min-height: 100vh;

    background-color: ${theme.colors.white};
  `,
  mq({
    gridTemplateColumns: [
      '1fr',
      undefined,
      undefined,
      'minmax(36rem, 42%) 1fr',
    ],
  })
);

export const AuthBrandPanelStyled = styled.aside(
  ({ theme }) => css`
    position: relative;

    color: ${theme.colors.white};

    background-color: ${theme.colors.clickable};
    background-image: linear-gradient(
      150deg,
      ${toColor(theme.colors.ink, 0.95)} 0%,
      ${theme.colors.clickable} 55%,
      ${toColor(theme.colors.highlight, 0.85)} 100%
    );

    /* Hero image card — contains a product screenshot inside the brand panel. */
    figure.hero-image {
      position: relative;

      display: block;
      margin: 3.2rem 0 0;
      overflow: hidden;

      background: ${theme.colors.white};
      border-radius: 1.2rem;
      box-shadow: 0 1.6rem 4rem ${toColor(theme.colors.ink, 0.3)};

      img {
        display: block;
        width: 100%;
        height: auto;
      }
    }

    > div {
      position: relative;
      z-index: 1;

      display: flex;
      flex-direction: column;
      gap: 3.2rem;
      min-height: 100%;
    }

    .eyebrow {
      display: inline-flex;
      gap: 1rem;
      align-items: center;
      margin: 0 0 2rem;

      color: ${theme.colors.secondary};
      font-weight: 600;
      font-size: 1.2rem;
      letter-spacing: 0.18rem;
      text-transform: uppercase;

      &:before {
        display: inline-block;
        width: 0.6rem;
        height: 0.6rem;

        background: ${theme.colors.secondary};
        border-radius: 50%;
        content: '';
      }
    }
    h1 {
      margin: 0 0 1.6rem;

      font-weight: 700;
      line-height: 1.1;
      letter-spacing: -0.05rem;
    }
    p.lede {
      margin: 0;

      color: ${toColor(theme.colors.white, 0.85)};
      line-height: 1.55;
    }
    p.trust {
      margin: 1.6rem 0 0;

      color: ${toColor(theme.colors.white, 0.7)};
      font-size: 1.3rem;
      letter-spacing: 0.02rem;
    }

    .brand-mark {
      display: inline-flex;
      gap: 1rem;
      align-items: center;

      font-weight: 700;
      font-size: 2rem;
      letter-spacing: -0.02rem;
      text-transform: lowercase;

      &:before {
        display: inline-block;
        width: 1.2rem;
        height: 1.2rem;

        background: ${theme.colors.secondary};
        border-radius: 50%;
        content: '';
      }
    }
  `,
  mq({
    h1: { fontSize: ['3.2rem', undefined, undefined, '4.4rem'] },
    'p.lede': { fontSize: ['1.6rem', undefined, undefined, '1.8rem'] },
    padding: ['4rem 3rem 5rem', undefined, undefined, '5rem 5rem'],
  })
);

export const AuthFeatureListStyled = styled.ul(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin: 0;
    padding: 0;

    list-style: none;

    li {
      display: grid;
      grid-template-columns: 4rem 1fr;
      gap: 1.4rem;
      align-items: start;
    }
    li figure {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 4rem;
      height: 4rem;
      margin: 0;

      color: ${theme.colors.white};
      font-weight: 700;
      font-size: 1.6rem;

      background: ${toColor(theme.colors.white, 0.15)};
      border: 1px solid ${toColor(theme.colors.white, 0.25)};
      border-radius: 50%;
    }
    li h3 {
      margin: 0 0 0.4rem;

      color: ${theme.colors.white};
      font-weight: 600;
      font-size: 1.7rem;
    }
    li p {
      margin: 0;

      color: ${toColor(theme.colors.white, 0.8)};
      font-size: 1.4rem;
      line-height: 1.55;
    }
  `
);

export const AuthFormPanelStyled = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: center;
    align-items: center;

    background-color: ${theme.colors.white};

    > div {
      width: 100%;
      max-width: 44rem;
    }

    h2 {
      margin: 0 0 0.8rem;

      color: ${theme.colors.ink};
      font-weight: 700;
      letter-spacing: -0.05rem;
    }
    > div > p {
      margin: 0 0 2rem;

      color: ${theme.colors.grayDark};
      font-size: 1.5rem;
      line-height: 1.55;
    }

    blockquote.testimonial {
      margin: 0 0 3.2rem;
      padding: 1.6rem 1.8rem;

      color: ${theme.colors.blackLight};
      font-size: 1.4rem;
      font-style: italic;
      line-height: 1.55;

      background: ${theme.colors.background};
      border-left: 3px solid ${theme.colors.clickable};
      border-radius: 0.6rem;

      cite {
        display: block;
        margin-top: 0.8rem;

        color: ${theme.colors.clickable};
        font-weight: 600;
        font-size: 1.2rem;
        font-style: normal;
        letter-spacing: 0.02rem;

        small {
          color: ${theme.colors.grayDark};
          font-weight: 400;
        }
      }
    }

    label span:first-of-type {
      font-weight: 500;
      letter-spacing: 0.04rem;
    }

    input[type='text'],
    input[type='email'],
    input[type='password'] {
      padding: 1.2rem 1.4rem;

      font-size: 1.5rem;

      border-radius: 0.8rem;

      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
      &:focus {
        border-color: ${theme.colors.clickable};
        outline: none;
        box-shadow: 0 0 0 4px ${toColor(theme.colors.clickable, 0.12)};
      }
    }

    button[type='submit'] {
      justify-content: center;
      width: 100%;
      padding: 1.3rem 1.6rem;

      font-weight: 600;
      font-size: 1.5rem;

      border-radius: 0.8rem;
      box-shadow: 0 0.6rem 1.6rem ${toColor(theme.colors.clickable, 0.25)};
      &:hover {
        transform: translateY(-1px);
      }
    }

    .auth-switch {
      margin-top: 2.8rem;
      padding-top: 2rem;

      color: ${theme.colors.grayDark};
      font-size: 1.4rem;
      text-align: center;

      border-top: 1px solid ${theme.colors.border};
      a {
        color: ${theme.colors.clickable};
        font-weight: 600;
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }

    .forgot-link {
      display: inline-block;
      margin-top: 0.8rem;

      color: ${theme.colors.grayDark};
      font-size: 1.3rem;
      text-decoration: none;
      &:hover {
        color: ${theme.colors.clickable};
      }
    }
  `,
  mq({
    h2: { fontSize: ['2.8rem', undefined, undefined, '3.4rem'] },
    padding: ['4rem 3rem', undefined, undefined, '4rem 6rem'],
  })
);

export const AuthFormGridStyled = styled(MaxWidthStyled)(
  () => css`
    gap: 1.6rem !important;
  `
);
